import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Sparkles, ExternalLink } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { homeTarget, ShareHomeButton } from '@/components/ShareHomeButton';
/**
 * SharedRecapTopicPage — public SPA route for a shared single-topic recap.
 *
 * Path: /share/recap-topic/:shareId
 *
 * Renders the frozen snapshot using the SAME visual language as the in-app
 * GlassSummaryModal on RecapPage.js: dark cosmic gradient background,
 * animated orbs, a glass-morphic panel with the badge + label +
 * HIGHLIGHT card + KEY POINTS list + TAKEAWAYS card. Consistency with the
 * in-app experience is the whole point of this page.
 */
export default function SharedRecapTopicPage() {
  const {
    t
  } = useTranslation();

  const { shareId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${window.location.origin}/api/recap/topic-share/${shareId}`)
      .then((r) => { if (alive) setDoc(r.data); })
      .catch(() => { if (alive) setError('This shared topic is unavailable.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [shareId]);

  const dark = true; // shared page always uses the cosmic dark palette
  const accent = doc?.bubble_color || 'rgba(99, 102, 241, 0.7)';

  if (loading) {
    return (
      <div className="min-h-dvh bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 flex items-center justify-center"
           data-testid="shared-recap-topic-loading">
        <div className="w-8 h-8 border-2 border-t-transparent border-indigo-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-dvh bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 flex flex-col items-center justify-center text-center p-4"
           style={{ color: 'rgba(255,255,255,0.95)' }}
           data-testid="shared-recap-topic-error">
        <p className="text-lg font-semibold mb-2">{t('generated.sharedrecaptopicpage_topic_unavailable_8a2a')}</p>
        <p className="text-sm opacity-70 mb-6">{error || 'This link may have expired.'}</p>
        <button
          onClick={() => navigate(homeTarget(), { replace: true })}
          className="px-4 py-2 rounded-full text-sm font-medium"
          style={{ background: '#6366f1', color: '#fff' }}
        >
          {t('generated.sharedrecaptopicpage_open_eili_cb64')}
        </button>
      </div>
    );
  }

  const keyPoints = doc.key_points || [];
  const takeaways = doc.takeaways || [];

  return (
    <div
      className="min-h-dvh relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950"
      style={{ color: 'rgba(255,255,255,0.95)' }}
      data-testid="shared-recap-topic-page"
    >
      <ShareHomeButton />
      {/* Frosted-glass overlay to match the summary modal's blurred backdrop */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'rgba(0, 0, 0, 0.35)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      />
      <div className="relative z-10 min-h-dvh flex flex-col items-center justify-center px-4 py-10">
        <div
          className="w-full"
          style={{ maxWidth: doc.orientation === 'portrait' ? 380 : 560 }}
        >
          <button
            onClick={() => navigate(homeTarget(), { replace: true })}
            className="inline-flex items-center gap-2 text-sm opacity-75 mb-5 hover:opacity-100"
            style={{ color: 'rgba(255,255,255,0.95)' }}
            data-testid="shared-recap-topic-back"
          >
            <ArrowLeft className="h-4 w-4" /> {t('generated.sharedrecaptopicpage_back_9811')}
          </button>

          {/* Glass panel — mirrors GlassSummaryModal from RecapPage */}
          <div
            className="relative w-full overflow-hidden rounded-3xl"
            style={{
              background: `linear-gradient(160deg,
                ${accent.replace('0.7', '0.7')} 0%,
                ${accent.replace('0.7', '0.5')} 40%,
                ${accent.replace('0.7', '0.6')} 100%)`,
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(255,255,255,0.25)',
              boxShadow: `
                0 32px 64px -16px rgba(0,0,0,0.5),
                inset 0 1px 0 rgba(255,255,255,0.3),
                inset 0 -1px 0 rgba(0,0,0,0.1)
              `,
            }}
          >
            {/* Top glass highlight */}
            <div
              className="absolute top-0 left-0 right-0 h-20 rounded-t-3xl pointer-events-none"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 100%)' }}
            />

            <div className="relative p-6 sm:p-8">
              {/* Badge */}
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold tracking-[0.15em] mb-4"
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
              >
                <Sparkles className="h-3 w-3" />
                {t('generated.sharedrecaptopicpage_weekly_recap_d394')}{doc.date_range ? `  ·  ${doc.date_range}` : ''}
              </div>

              {/* Topic label as headline */}
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="p-2.5 rounded-xl"
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                >
                  <Sparkles className="h-6 w-6" style={{ color: '#fff' }} />
                </div>
                <h1
                  className="text-2xl sm:text-3xl font-bold tracking-tight"
                  style={{ color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
                  data-testid="shared-recap-topic-label"
                >
                  {doc.label}
                </h1>
              </div>

              {/* Highlight card */}
              {doc.highlight && (
                <div
                  className="rounded-2xl p-5 mb-5"
                  style={{
                    background: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  data-testid="shared-recap-topic-highlight-wrap"
                >
                  <h3
                    className="text-[10px] uppercase tracking-widest mb-2 font-semibold"
                    style={{ color: 'rgba(255,255,255,0.65)' }}
                  >
                    {t('generated.sharedrecaptopicpage_highlight_f0a5')}
                  </h3>
                  <p
                    className="text-lg leading-relaxed"
                    style={{ color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                    data-testid="shared-recap-topic-highlight"
                  >
                    {doc.highlight}
                  </p>
                </div>
              )}

              {/* Key Points */}
              {keyPoints.length > 0 && (
                <div className="mb-5" data-testid="shared-recap-topic-keypoints">
                  <h3
                    className="text-[10px] uppercase tracking-widest mb-3 font-semibold"
                    style={{ color: 'rgba(255,255,255,0.65)' }}
                  >
                    {t('generated.sharedrecaptopicpage_key_points_d9b6')}
                  </h3>
                  <ul className="space-y-3">
                    {keyPoints.map((point, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span
                          className="w-2 h-2 rounded-full mt-2 shrink-0"
                          style={{ background: 'rgba(255,255,255,0.7)' }}
                        />
                        <span
                          className="leading-relaxed"
                          style={{ color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}
                        >
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Takeaways card */}
              {takeaways.length > 0 && (
                <div
                  className="rounded-2xl p-5 mb-4"
                  style={{
                    background: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  data-testid="shared-recap-topic-takeaways"
                >
                  <h3
                    className="text-[10px] uppercase tracking-widest mb-3 font-semibold"
                    style={{ color: 'rgba(255,255,255,0.65)' }}
                  >
                    {t('generated.sharedrecaptopicpage_takeaways_3811')}
                  </h3>
                  <ul className="space-y-3">
                    {takeaways.map((t, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-lg leading-none">✨</span>
                        <span
                          className="font-medium leading-relaxed"
                          style={{ color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                        >
                          {t}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Footer CTA */}
              <div
                className="mt-6 pt-5 flex items-center justify-between flex-wrap gap-3"
                style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}
              >
                <span className="text-sm italic" style={{ color: 'rgba(255,255,255,0.7)' }}>{t('generated.sharedrecaptopicpage_eili_4800')}</span>
                <button
                  onClick={() => navigate('/recap')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-[1.02]"
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(8px)',
                  }}
                  data-testid="shared-recap-topic-open-in-app"
                >
                  {t('generated.sharedrecaptopicpage_see_your_own_8916')} <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
